/**
 * useAccountReturns — ViewModel for the /account/returns screen
 * (Kiran 2026-06-15). Customer-side paginated list of the customer's
 * own returns. Mirrors useAccountOrders shape.
 */

import { useCallback } from 'react'
import { useAuth } from '../../context/AuthContext'
import { APIROUTES } from '../../constants/routes.constants'
import { parseApiError } from '../../utils/api/parseApiError'
import { usePaginatedList } from '../usePaginatedList'

export interface AccountReturnDto {
  readonly id: string
  readonly order_id: string
  readonly reason: string
  readonly status: string
  readonly requested_at: string
  readonly organisation_name: string | null
}

interface ListBody {
  readonly success: true
  readonly data: {
    readonly items: AccountReturnDto[]
    readonly total: number
    readonly page: number
    readonly pageSize: number
  }
}

export interface UseAccountReturnsReturn {
  readonly returns: readonly AccountReturnDto[]
  readonly total: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly error: string | null
  readonly setPage: (n: number) => void
  readonly reload: () => void
}

export function useAccountReturns(): UseAccountReturnsReturn {
  const { authedFetch } = useAuth()

  const paged = usePaginatedList<AccountReturnDto, undefined>({
    fetcher: useCallback(
      async (page, pageSize, signal) => {
        const url = `${APIROUTES.ACCOUNTRETURNS}?page=${page}&pageSize=${pageSize}`
        const res = await authedFetch(url, { signal })
        if (!res.ok) {
          throw new Error((await parseApiError(res)).message)
        }
        const body = (await res.json()) as ListBody
        return { items: body.data.items, total: body.data.total }
      },
      [authedFetch]
    )
  })

  return {
    returns: paged.items,
    total: paged.total,
    page: paged.page,
    pageSize: paged.pageSize,
    isLoading: paged.isLoading,
    error: paged.error,
    setPage: paged.setPage,
    reload: paged.reload
  }
}
